import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Table,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Modal,
  Input,
  Label,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FaBarcode, FaBox, FaBoxes, FaReceipt, FaSearch, FaTag, FaTrash } from "react-icons/fa";

import { customerReturnActions } from "../../sagas/customerReturnSlice";
import { clientActions } from "../../sagas/clientSlice";
import { formatPrice } from "../../helpers/utility";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { productActions } from "../../sagas/productSlice";
import { commercialActions } from "../../sagas/commercialSlice";
import { entrepotActions } from "../../sagas/entrepotSlice";
import { transporteurActions } from "../../sagas/transporteurSlice";

const CustomerReturnForm = ({ customerReturn, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEditing = Boolean(customerReturn);

  const { currentReturn, newReturnNumber, loading } = useSelector(
    (state) => state.customerReturns
  );
  const { clients } = useSelector((state) => state.clients);
  const { loading: deliveryLoading } = useSelector(
    (state) => state.deliveryNotes || {}
  );
  const {
    products,
    loading: productLoading,
    totalPages,
    totalElements,
    pageSize,
  } = useSelector((state) => state.products);
  const unites = useSelector((state) => state.products.unites);
  const { entrepots } = useSelector((state) => state.entrepots);
  const loadedReturn = currentReturn?.body || currentReturn || null;
  const returnRecord = loadedReturn || customerReturn || null;

  const [returnSourceMode, setReturnSourceMode] = useState("MANUAL");
  const [chooseProductModal, setChooseProductModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketNumber, setTicketNumber] = useState("");
  const [loadedTicketNumber, setLoadedTicketNumber] = useState("");
  const getLocalISODate = () => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().split("T")[0];
  };

  const canBeUpdated =
    (isEditing && returnRecord?.statut === "BROUILLON") || !isEditing;

  const toggleChooseProductModal = () =>
    setChooseProductModal((prev) => !prev);

  useEffect(() => {
    dispatch(clientActions.search({ query: "" }));

    if (isEditing) {
      dispatch(customerReturnActions.getById({ id: customerReturn.id }));
    }
  }, [dispatch, id, isEditing, customerReturn?.id]);

  useEffect(() => {
    dispatch(productActions.findAllUnites());
    dispatch(customerReturnActions.generateNumero());
    dispatch(commercialActions.list({ query: "" }));
    dispatch(entrepotActions.list());
    dispatch(transporteurActions.list());
  }, [dispatch]);

  useEffect(() => {
    const existingNumBL = returnRecord?.numBL || "";
    if (!existingNumBL) return;

    setTicketNumber(existingNumBL);
    setLoadedTicketNumber(existingNumBL);
    setReturnSourceMode("TICKET");
  }, [returnRecord?.numBL]);

  useEffect(() => {
    dispatch(
      productActions.findAll({
        search: modalSearchTerm,
        page: currentPage - 1,
        size: 10,
      })
    );
  }, [dispatch, currentPage, chooseProductModal, modalSearchTerm]);

  const handleClientChange = (clientId, setFieldValue) => {
    setFieldValue("clientId", clientId);
    setSelectedItems([]);
  };

  const clientOptions =
    clients?.map((client) => ({
      value: client.id,
      label:
        client.typeClient === "PARTICULIER"
          ? `${client.nom} ${client.prenom || ""}`.trim()
          : client.entreprise,
    })) ?? [];

  const handleModalSearchChange = (e) => {
    const value = e.target.value;
    setModalSearchTerm(value);
    setCurrentPage(1);
  };

  const validationSchema = Yup.object().shape({
    dateOperation: Yup.date().required(
      t("customerReturns.form.validation.dateRequired")
    ),
    modeRemboursement: Yup.string().required(),
    lignes: Yup.array().min(
      1,
      t("customerReturns.form.validation.itemsRequired")
    ),
  });

  const calculateTotals = (lines) => {
    let totalHT = 0;
    let totalTVA = 0;
    let totalTTC = 0;

    (lines || []).forEach((line) => {
      const qte = Number(line.quantiteRetournee) || 0;
      const ttc = Number(line.prixUnitaireTTC) || 0;
      const taux = Number(line.tauxTVA) || 0;

      const lineTotalTTC = qte * ttc;
      const lineHT = taux > 0 ? lineTotalTTC / (1 + taux) : lineTotalTTC;
      const lineTVA = lineTotalTTC - lineHT;

      totalTTC += lineTotalTTC;
      totalHT += lineHT;
      totalTVA += lineTVA;
    });

    return {
      montantHT: totalHT,
      montantTVA: totalTVA,
      montantTTC: totalTTC,
    };
  };

  const recalcLineAmounts = (line) => {
    const qte = Number(line.quantiteRetournee) || 0;
    const prixTTC = Number(line.prixUnitaireTTC) || 0;
    const taux = Number(line.tauxTVA) || 0;

    const montantTTC = qte * prixTTC;
    const montantHT = taux > 0 ? montantTTC / (1 + taux) : montantTTC;
    const montantTVA = montantTTC - montantHT;

    return {
      ...line,
      montantHT,
      montantTVA,
      montantTTC,
    };
  };

  const mapDeliveryLineToReturnLine = (line) =>
    recalcLineAmounts({
      article: line.articleId,
      articleId: line.articleId,
      articleLibelle: line.articleNom || line.articleLibelle || "-",
      articleCode: line.articleCode || "",
      quantiteRetournee:
        Number(
          line.quantiteLivree ?? line.quantite ?? line.quantiteRetournee ?? 1
        ) || 1,
      quantiteOriginale:
        Number(
          line.quantiteLivree ?? line.quantite ?? line.quantiteOriginale ?? 1
        ) || 1,
      prixUnitaireHT: Number(line.prixUnitaireHT ?? 0),
      prixUnitaireTTC: Number(line.prixUnitaireTTC ?? 0),
      tauxTVA: Number(line.tauxTVA ?? 0.2),
      unite: line.unite || "",
    });

  const handleSubmit = (values, { setSubmitting }) => {
    const totals = calculateTotals(values.lignes);
    const dataToSubmit = {
      ...values,
      ...totals,
      numBL: values.numBL || loadedTicketNumber || "",
      isPassager: values.clientId ? false : true,
    };

    if (isEditing) {
      dispatch(
        customerReturnActions.update({
          id: returnRecord?.id,
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.success"),
              text: t("customerReturns.messages.updateSuccess"),
            });
            onClose();
          },
          onError: () => {
            Swal.fire({
              icon: "error",
              title: t("common.error"),
              text: t("customerReturns.messages.saveError"),
            });
            setSubmitting(false);
          },
        })
      );
    } else {
      dispatch(
        customerReturnActions.create({
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.success"),
              text: t("customerReturns.messages.updateSuccess"),
            });
            onClose();
          },
          onError: () => {
            Swal.fire({
              icon: "error",
              title: t("common.error"),
              text: t("customerReturns.messages.saveError"),
            });
            setSubmitting(false);
          },
        })
      );
    }
  };

  const initialValues =
    isEditing && returnRecord
      ? {
          numeroRetour: returnRecord.numeroRetour || "",
          dateOperation:
            returnRecord.dateOperation || getLocalISODate(),
          clientId: returnRecord.clientId || null,
          entrepotId: returnRecord.entrepotId || null,
          numBL: returnRecord.numBL || "",
          modeRemboursement: returnRecord.modeRemboursement || "AVOIR",
          isPassager: returnRecord.passager || false,
          lignes:
            returnRecord.lignes?.map((line) =>
              recalcLineAmounts({
                articleId: line.articleId,
                articleLibelle: line.articleNom,
                articleCode: line.articleCode,
                quantiteRetournee: line.quantiteRetournee,
                quantiteOriginale:
                  line.quantiteOriginale ?? line.quantiteRetournee ?? 1,
                prixUnitaireHT: line.prixUnitaireHT,
                prixUnitaireTTC: line.prixUnitaireTTC,
                tauxTVA: line.tauxTVA,
                unite: line.unite || line.article?.unite || "",
              })
            ) || [],
        }
      : {
          numeroRetour: newReturnNumber,
          dateOperation: getLocalISODate(),
          clientId: null,
          entrepotId: null,
          numBL: "",
          modeRemboursement: "AVOIR",
          lignes: [],
        };

  if (loading && isEditing) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center my-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  const entrepotOptions =
    entrepots?.map((entrepot) => ({
      value: entrepot.id,
      label: `${entrepot.code} - ${entrepot.libelle}`,
    })) ?? [];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleSubmit: submitFormik,
              isSubmitting,
              setFieldValue,
            }) => {
              const totals = calculateTotals(values.lignes);

              const handleTicketNumberChange = (nextValue) => {
                const normalizedNext = (nextValue || "").trim();
                const normalizedLoaded = (loadedTicketNumber || "").trim();
                const hasChangedLoadedBL =
                  normalizedLoaded &&
                  normalizedNext.toUpperCase() !== normalizedLoaded.toUpperCase();

                setTicketNumber(nextValue);

                if (isEditing && hasChangedLoadedBL) {
                  setFieldValue("lignes", []);
                  setFieldValue("numBL", "");
                  setLoadedTicketNumber("");
                }
              };

              const handleFetchTicket = () => {
                const reference = (ticketNumber || "").trim();
                if (!reference) {
                  Swal.fire({
                    icon: "warning",
                    title: t("sale.error"),
                    text: "Veuillez saisir un numero de ticket/BL.",
                  });
                  return;
                }

                dispatch(
                  deliveryActions.findByReference({
                    reference,
                    onSuccess: (result) => {
                      const bl =
                        result?.body ||
                        result?.data?.body ||
                        result?.data ||
                        result;
                      const lignes = Array.isArray(bl?.lignes) ? bl.lignes : [];

                      if (!lignes.length) {
                        Swal.fire({
                          icon: "info",
                          title: t("common.information"),
                          text: "Aucune ligne trouvee pour ce ticket.",
                        });
                        return;
                      }

                      const mappedLines = lignes.map(mapDeliveryLineToReturnLine);
                      setFieldValue("lignes", mappedLines);
                      setFieldValue("numBL", bl?.numero || reference);

                      if (bl?.clientId) {
                        setFieldValue("clientId", bl.clientId);
                      }

                      setLoadedTicketNumber(bl?.numero || reference);
                      setTicketNumber(bl?.numero || reference);
                      toast.success("Ticket charge avec succes.");
                    },
                    onError: () => {
                      Swal.fire({
                        icon: "error",
                        title: t("common.error"),
                        text: "Ticket introuvable.",
                      });
                    },
                  })
                );
              };

              return (
                <Form onSubmit={submitFormik}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <CardBody>
                          <Row>
                            <Col md={3}>
                              <div className="mb-3">
                                <label className="form-label">
                                  {t("customerReturns.form.fields.returnNumber")}
                                </label>
                                <Field
                                  name="numeroRetour"
                                  type="text"
                                  className="form-control"
                                  placeholder={t(
                                    "customerReturns.form.placeholders.returnNumber"
                                  )}
                                  disabled
                                />
                              </div>
                            </Col>
                            <Col md={3}>
                              <div className="mb-3">
                                <label className="form-label">
                                  {t("customerReturns.form.fields.client")}
                                </label>

                                <Select
                                  value={clientOptions.find(
                                    (option) => option.value === values.clientId
                                  )}
                                  onChange={(option) =>
                                    handleClientChange(option?.value ?? null, setFieldValue)
                                  }
                                  options={clientOptions}
                                  placeholder={t(
                                    "customerReturns.form.placeholders.selectClient"
                                  )}
                                  isDisabled={!canBeUpdated}
                                  isSearchable
                                  className={
                                    errors.clientId && touched.clientId
                                      ? "is-invalid"
                                      : ""
                                  }
                                  menuPortalTarget={
                                    typeof window !== "undefined" ? document.body : null
                                  }
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  }}
                                />

                                {errors.clientId && touched.clientId && (
                                  <div className="invalid-feedback d-block">
                                    {errors.clientId}
                                  </div>
                                )}
                              </div>
                            </Col>

                            <Col md={3}>
                              <div className="mb-3">
                                <label className="form-label">
                                  {t("customerReturns.form.fields.returnDate")}
                                </label>
                                <Field
                                  name="dateOperation"
                                  type="date"
                                  className={`form-control ${
                                    errors.dateOperation && touched.dateOperation
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  disabled={!canBeUpdated}
                                />
                                <ErrorMessage
                                  name="dateOperation"
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </div>
                            </Col>
                            <Col md="3">
                              <Label htmlFor="entrepotId">
                                {t("bonLivraison.entrepot")}:
                              </Label>
                              <Select
                                id="entrepotId"
                                options={entrepotOptions}
                                value={entrepotOptions.find(
                                  (option) => option.value === values.entrepotId
                                )}
                                onChange={(selectedOption) =>
                                  setFieldValue(
                                    "entrepotId",
                                    selectedOption?.value ?? null
                                  )
                                }
                                placeholder={t("bonLivraison.selectEntrepot")}
                                isSearchable
                                isDisabled={!canBeUpdated}
                              />
                              {errors.entrepotId && touched.entrepotId && (
                                <div className="text-danger small mt-1">
                                  {errors.entrepotId}
                                </div>
                              )}
                            </Col>
                          </Row>

                          <Row>
                            <Col lg={12}>
                              <div className="d-flex gap-2 mb-3">
                                <Button
                                  type="button"
                                  color={
                                    returnSourceMode === "MANUAL"
                                      ? "primary"
                                      : "light"
                                  }
                                  onClick={() => setReturnSourceMode("MANUAL")}
                                  disabled={!canBeUpdated}
                                >
                                  Sans ticket
                                </Button>
                                <Button
                                  type="button"
                                  color={
                                    returnSourceMode === "TICKET"
                                      ? "primary"
                                      : "light"
                                  }
                                  onClick={() => setReturnSourceMode("TICKET")}
                                  disabled={!canBeUpdated}
                                >
                                  Avec ticket
                                </Button>
                              </div>

                              {returnSourceMode === "TICKET" ? (
                                <div className="mb-4">
                                  <Label className="form-label">
                                    <FaReceipt className="me-2" />
                                    Numero ticket / BL
                                  </Label>
                                  <div className="d-flex gap-2">
                                    <Input
                                      type="text"
                                      value={ticketNumber}
                                      onChange={(e) =>
                                        handleTicketNumberChange(e.target.value)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.preventDefault();
                                          handleFetchTicket();
                                        }
                                      }}
                                      placeholder="Ex: BL-000123"
                                      disabled={!canBeUpdated || deliveryLoading}
                                    />
                                    <Button
                                      type="button"
                                      color="primary"
                                      onClick={handleFetchTicket}
                                      disabled={!canBeUpdated || deliveryLoading}
                                    >
                                      {deliveryLoading ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        <FaSearch />
                                      )}
                                    </Button>
                                  </div>
                                  {loadedTicketNumber ? (
                                    <small className="text-success d-block mt-2">
                                      Ticket charge: {loadedTicketNumber}
                                    </small>
                                  ) : null}
                                </div>
                              ) : null}

                              {selectedItems.length > 0 && (
                                <div className="mb-4">
                                  <h5>{t("customerReturns.form.invoiceItems")}</h5>
                                  <div className="table-responsive">
                                    <Table className="table-centered table-nowrap mb-0">
                                      <thead>
                                        <tr>
                                          <th>{t("customerReturns.form.columns.item")}</th>
                                          <th>{t("customerReturns.form.columns.quantity")}</th>
                                          <th>{t("customerReturns.form.columns.unite")}</th>
                                          <th>{t("customerReturns.form.columns.unitPrice")}</th>
                                          <th>{t("customerReturns.form.columns.total")}</th>
                                          <th>{t("customerReturns.form.columns.actions")}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {selectedItems.map((item, index) => (
                                          <tr key={index}>
                                            <td>{item.articleNom}</td>
                                            <td>{item.quantite ?? item.quantiteLivree}</td>
                                            <td>
                                              {
                                                unites.find((u) => u.code === item.unite)
                                                  ?.libelle
                                              }
                                            </td>
                                            <td>{formatPrice(item.prixUnitaireTTC)}</td>
                                            <td>{formatPrice(item.montantTTC)}</td>
                                            <td>
                                              <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => {
                                                  const isItemAlreadyAdded = values.lignes.some(
                                                    (line) =>
                                                      line.article === item.articleId ||
                                                      line.articleId === item.articleId
                                                  );

                                                  if (isItemAlreadyAdded) {
                                                    toast.warning(
                                                      t(
                                                        "customerReturns.messages.itemAlreadyAdded"
                                                      )
                                                    );
                                                  } else {
                                                    const newLine = recalcLineAmounts({
                                                      articleId: item.articleId,
                                                      article: item.articleId,
                                                      articleLibelle: item.articleNom,
                                                      articleCode: item.articleCode || "",
                                                      quantiteRetournee: 1,
                                                      quantiteOriginale:
                                                        item.quantite || item.quantiteLivree,
                                                      prixUnitaireHT: item.prixUnitaireHT,
                                                      prixUnitaireTTC: item.prixUnitaireTTC,
                                                      tauxTVA: item.tauxTVA,
                                                      unite: item.unite,
                                                    });
                                                    setFieldValue("lignes", [
                                                      ...values.lignes,
                                                      newLine,
                                                    ]);
                                                  }
                                                }}
                                                disabled={values.lignes.some(
                                                  (line) =>
                                                    line.article === item.articleId ||
                                                    line.articleId === item.articleId
                                                )}
                                              >
                                                <i className="bx bx-plus"></i>{" "}
                                                {values.lignes.some(
                                                  (line) =>
                                                    line.article === item.articleId ||
                                                    line.articleId === item.articleId
                                                )
                                                  ? t(
                                                      "customerReturns.form.buttons.alreadyAdded"
                                                    )
                                                  : t(
                                                      "customerReturns.form.buttons.add"
                                                    )}
                                              </Button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                </div>
                              )}

                              {returnSourceMode === "MANUAL" && (
                                <div className="mt-2">
                                  <Row>
                                    <Col md={12}>
                                      <div className="mb-3">
                                        <Button
                                          color="primary"
                                          onClick={toggleChooseProductModal}
                                          disabled={!canBeUpdated}
                                        >
                                          <i className="bx bx-plus me-1"></i>
                                          {t("customerReturns.selectProduct")}
                                        </Button>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              )}

                              <FieldArray name="lignes">
                                {({ remove }) => (
                                  <div className="table-responsive">
                                    <table className="table table-hover table-striped table-bordered">
                                      <thead>
                                        <tr>
                                          <th>{t("customerReturns.form.columns.item")}</th>
                                          <th>
                                            {t("customerReturns.form.columns.returnedQte")}
                                          </th>
                                          <th>{t("customerReturns.form.columns.unite")}</th>
                                          <th>{t("customerReturns.form.columns.unitPrice")}</th>
                                          <th>{t("customerReturns.form.columns.total")}</th>
                                          <th>{t("customerReturns.form.columns.actions")}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {values.lignes.length === 0 ? (
                                          <tr>
                                            <td colSpan="6" className="text-center">
                                              {t("customerReturns.form.noItems")}
                                            </td>
                                          </tr>
                                        ) : (
                                          values.lignes.map((line, index) => {
                                            const lineTotal = line.montantTTC || 0;
                                            return (
                                              <tr key={index}>
                                                <td>
                                                  {line.articleLibelle || "Article inconnu"}
                                                </td>
                                                <td>
                                                  <Field
                                                    name={`lignes.${index}.quantiteRetournee`}
                                                    type="number"
                                                    min="1"
                                                    onFocus={(e) => e.target.select()}
                                                    className="form-control"
                                                    style={{ width: "120px" }}
                                                    disabled={!canBeUpdated}
                                                    onChange={(e) => {
                                                      const qte =
                                                        Number(e.target.value) || 0;
                                                      const updatedLine =
                                                        recalcLineAmounts({
                                                          ...values.lignes[index],
                                                          quantiteRetournee: qte,
                                                        });

                                                      setFieldValue(
                                                        `lignes.${index}`,
                                                        updatedLine
                                                      );
                                                    }}
                                                  />
                                                </td>
                                                <td>
                                                  {unites.find((u) => u.code === line.unite)
                                                    ?.libelle || line.unite}
                                                </td>
                                                <td>
                                                  <Field
                                                    name={`lignes.${index}.prixUnitaireTTC`}
                                                    type="number"
                                                    step="0.01"
                                                    onFocus={(e) => e.target.select()}
                                                    className="form-control"
                                                    style={{ width: "150px" }}
                                                    disabled={!canBeUpdated}
                                                    onChange={(e) => {
                                                      const prix =
                                                        Number(e.target.value) || 0;
                                                      const updatedLine =
                                                        recalcLineAmounts({
                                                          ...values.lignes[index],
                                                          prixUnitaireTTC: prix,
                                                        });

                                                      setFieldValue(
                                                        `lignes.${index}`,
                                                        updatedLine
                                                      );
                                                    }}
                                                  />
                                                </td>
                                                <td>{formatPrice(lineTotal)}</td>
                                                <td>
                                                  <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => remove(index)}
                                                    disabled={!canBeUpdated}
                                                  >
                                                    <FaTrash />
                                                  </Button>
                                                </td>
                                              </tr>
                                            );
                                          })
                                        )}
                                      </tbody>
                                      <tfoot>
                                        {values.lignes.length > 0 && (
                                          <>
                                            <tr>
                                              <th colSpan="4" className="text-end">
                                                {t(
                                                  "customerReturns.form.totals.totalHT"
                                                )}
                                                :
                                              </th>
                                              <th>{formatPrice(totals.montantHT)}</th>
                                              <th />
                                            </tr>

                                            <tr>
                                              <th colSpan="4" className="text-end">
                                                {t(
                                                  "customerReturns.form.totals.totalVAT"
                                                )}
                                                :
                                              </th>
                                              <th>{formatPrice(totals.montantTVA)}</th>
                                              <th />
                                            </tr>

                                            <tr>
                                              <th colSpan="4" className="text-end">
                                                {t(
                                                  "customerReturns.form.totals.totalTTC"
                                                )}
                                                :
                                              </th>
                                              <th>{formatPrice(totals.montantTTC)}</th>
                                              <th />
                                            </tr>
                                          </>
                                        )}
                                      </tfoot>
                                    </table>
                                  </div>
                                )}
                              </FieldArray>
                              {errors.lignes && typeof errors.lignes === "string" && (
                                <div className="text-danger mt-2">{errors.lignes}</div>
                              )}
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col>
                      <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={onClose}>
                          {t("customerReturns.form.buttons.cancel")}
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isSubmitting || !canBeUpdated}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner size="sm" className="me-2" />{" "}
                              {t("customerReturns.form.buttons.saving")}
                            </>
                          ) : (
                            t("customerReturns.form.buttons.save")
                          )}
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Modal
                    isOpen={chooseProductModal}
                    toggle={toggleChooseProductModal}
                    size="xl"
                  >
                    <ModalHeader toggle={toggleChooseProductModal}>
                      {t("product.chooseProduct")}
                    </ModalHeader>
                    <ModalBody>
                      <Row>
                        <Col md={12}>
                          <Input
                            type="text"
                            placeholder={t("product.searchPlaceholder")}
                            value={modalSearchTerm}
                            onChange={handleModalSearchChange}
                            className="me-2"
                          />
                        </Col>
                      </Row>
                      <div
                        className="cart-container"
                        style={{ height: "300px", overflowY: "auto" }}
                      >
                        <Table hover bordered className="align-middle mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th className="align-middle">
                                <FaBarcode className="me-2 text-primary" size={12} />
                                {t("product.code")}
                              </th>
                              <th className="align-middle">
                                <FaBox className="me-2 text-primary" size={12} />
                                {t("product.description")}
                              </th>
                              <th className="align-middle text-end">
                                <FaTag className="me-2 text-primary" size={12} />
                                {t("product.salePrice")}
                              </th>
                              <th className="align-middle text-center">
                                <FaBoxes className="me-2 text-primary" size={12} />
                                {t("product.stockPhysique")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {productLoading ? (
                              <tr>
                                <td colSpan="4" className="text-center py-4">
                                  <Spinner size="sm" />
                                </td>
                              </tr>
                            ) : !products || products.length === 0 ? (
                              <tr>
                                <td colSpan="4" className="text-center py-4">
                                  <div className="text-muted">
                                    {t("product.noProducts")}
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              products.map((product) => (
                                <tr
                                  key={product.id}
                                  onClick={() => {
                                    const isProductAlreadyAdded = values.lignes.some(
                                      (line) =>
                                        line.article === product.id ||
                                        line.articleId === product.id
                                    );

                                    if (isProductAlreadyAdded) {
                                      toast.warning(
                                        t("customerReturns.messages.itemAlreadyAdded")
                                      );
                                    } else {
                                      const newLine = recalcLineAmounts({
                                        article: product.id,
                                        articleId: product.id,
                                        articleLibelle: product.libelle,
                                        articleCode: product.code,
                                        quantiteRetournee: 1,
                                        quantiteOriginale: 1,
                                        prixUnitaireHT:
                                          product.prixVenteHT ||
                                          product.prixVenteTTC1 /
                                            (1 + (product.tauxTVA || 0.2)),
                                        prixUnitaireTTC: product.prixVenteTTC1,
                                        tauxTVA: product.tauxTVA || 0.2,
                                        unite: product.unite,
                                      });
                                      setFieldValue("lignes", [
                                        ...values.lignes,
                                        newLine,
                                      ]);
                                      toggleChooseProductModal();
                                    }
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    backgroundColor: values.lignes.some(
                                      (line) =>
                                        line.article === product.id ||
                                        line.articleId === product.id
                                    )
                                      ? "#f8f9fa"
                                      : "inherit",
                                  }}
                                  className={
                                    values.lignes.some(
                                      (line) =>
                                        line.article === product.id ||
                                        line.articleId === product.id
                                    )
                                      ? "text-muted"
                                      : ""
                                  }
                                >
                                  <td className="align-middle">{product.code}</td>
                                  <td className="align-middle">
                                    {product.libelle}
                                    {values.lignes.some(
                                      (line) =>
                                        line.article === product.id ||
                                        line.articleId === product.id
                                    ) && (
                                      <span className="ms-2 badge bg-light text-dark">
                                        {t("customerReturns.form.alreadyAdded")}
                                      </span>
                                    )}
                                  </td>
                                  <td className="align-middle text-end">
                                    {formatPrice(product.prixVenteTTC1)}
                                  </td>
                                  <td className="align-middle text-center">
                                    {product.stockPhysique}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">
                          {t("product.showing", {
                            start: (currentPage - 1) * pageSize + 1,
                            end: Math.min(currentPage * pageSize, totalElements),
                            total: totalElements,
                            currentPage,
                            totalPages,
                          })}
                        </small>
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        color="secondary"
                        onClick={() => {
                          setModalSearchTerm("");
                          setCurrentPage(1);
                          toggleChooseProductModal();
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                    </ModalFooter>
                  </Modal>
                </Form>
              );
            }}
          </Formik>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CustomerReturnForm;
